import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CnaeOption {
  value: string;
  label: string;
  description: string;
}

interface CnaeSearchResponse {
  options: CnaeOption[];
  totalCount: number;
}

const DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delay = DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

async function fetchCnae(query: string): Promise<CnaeSearchResponse> {
  const { data, error } = await supabase.functions.invoke<CnaeSearchResponse>("fiscal-reference-data", {
    body: {
      type: "cnae",
      q: query,
    },
  });

  if (error) throw error;

  return data ?? { options: [], totalCount: 0 };
}

export function useFocusCnaeSearch({
  query,
  enabled = true,
}: {
  query: string;
  enabled?: boolean;
}) {
  const debouncedQuery = useDebouncedValue(query);
  const shouldSearch = enabled && debouncedQuery.trim().length >= 2;

  return useQuery<CnaeSearchResponse>({
    queryKey: ["focus-cnae", debouncedQuery],
    enabled: shouldSearch,
    staleTime: 1000 * 60 * 10,
    queryFn: () => fetchCnae(debouncedQuery.trim()),
  });
}
